/**
 * Cookie consent state + analytics gating helper.
 *
 * Source of truth: the `cait_cookie_consent` first-party cookie set by the
 * backend (`POST /api/privacy/cookie-consent` per FR-P-1). The cookie value
 * is URL-encoded JSON of shape `{v, n, a, m}` per Spec §5.9.
 *
 * Analytics SDKs (PostHog, Sentry session replay) MUST call
 * `analyticsConsentGranted()` before initializing — for EU traffic this
 * blocks ingestion until consent (D24).
 */

import { apiGet, apiPost } from "./api";

export const CONSENT_COOKIE_NAME = "cait_cookie_consent";
export const ANONYMOUS_ID_COOKIE_NAME = "cait_anonymous_id";
export const CONSENT_VERSION = "2026-05-07"; // bump when banner copy or categories change

export interface CookieConsent {
  consentVersion: string;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  anonymousId: string;
}

interface CookieConsentResponse {
  consent_version: string;
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  anonymous_id: string;
  recorded_at: string;
}

interface RawCookiePayload {
  v: string;
  n: boolean;
  a: boolean;
  m: boolean;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  for (const c of cookies) {
    const idx = c.indexOf("=");
    if (idx === -1) continue;
    const k = c.slice(0, idx);
    if (k === name) {
      return decodeURIComponent(c.slice(idx + 1));
    }
  }
  return null;
}

/** Synchronous read of the consent cookie set by our own backend.
 *  Returns null if no decision has been recorded yet (banner should show). */
export function readConsentFromCookie(): CookieConsent | null {
  const raw = readCookie(CONSENT_COOKIE_NAME);
  const anonymousId = readCookie(ANONYMOUS_ID_COOKIE_NAME);
  if (!raw || !anonymousId) return null;
  try {
    const parsed = JSON.parse(raw) as RawCookiePayload;
    if (typeof parsed !== "object" || parsed === null) return null;
    return {
      consentVersion: String(parsed.v ?? ""),
      necessary: true,
      analytics: Boolean(parsed.a),
      marketing: Boolean(parsed.m),
      anonymousId,
    };
  } catch {
    return null;
  }
}

/** Authoritative server read (Day 3+ also persists to cookie_consents
 *  table; cookie alone is the fast path). */
export async function fetchConsent(): Promise<CookieConsent | null> {
  const response = await apiGet<CookieConsentResponse | null>(
    "/api/privacy/cookie-consent",
  );
  if (!response) return null;
  return {
    consentVersion: response.consent_version,
    necessary: true,
    analytics: response.analytics,
    marketing: response.marketing,
    anonymousId: response.anonymous_id,
  };
}

export interface ConsentChoice {
  analytics: boolean;
  marketing: boolean;
}

/** POST the user's banner choice; backend sets the cookies. */
export async function recordConsent(
  choice: ConsentChoice,
): Promise<CookieConsent> {
  const response = await apiPost<CookieConsentResponse>(
    "/api/privacy/cookie-consent",
    { ...choice, consent_version: CONSENT_VERSION },
  );
  return {
    consentVersion: response.consent_version,
    necessary: true,
    analytics: response.analytics,
    marketing: response.marketing,
    anonymousId: response.anonymous_id,
  };
}

/** Has the user granted analytics consent? Used by PostHog / Sentry session
 *  replay init code to gate ingestion. Belt-and-braces conservative: returns
 *  false if consent is unknown or stale. */
export function analyticsConsentGranted(): boolean {
  const consent = readConsentFromCookie();
  return Boolean(
    consent && consent.analytics && consent.consentVersion === CONSENT_VERSION,
  );
}

/** True if the consent we have on file is older than the current banner
 *  version — banner should re-prompt. */
export function consentIsStale(): boolean {
  const consent = readConsentFromCookie();
  if (!consent) return true;
  return consent.consentVersion !== CONSENT_VERSION;
}
